import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import compoundData from "../../data/compoundData";
import "./CompoundEducation.css";

const RECEPTOR_WIDTHS = {
    "None":      "0%",
    "Low":       "22%",
    "Moderate":  "50%",
    "High":      "75%",
    "Very High": "95%",
    "Variable":  "40%",
};

function ReceptorBar({ label, affinity, accentColor }) {
    const width = RECEPTOR_WIDTHS[affinity] || "0%";
    return (
        <div className="ce-receptor-row">
            <span className="ce-receptor-label">{label}</span>
            <div className="ce-receptor-track">
                <div
                    className="ce-receptor-fill"
                    style={{ width, backgroundColor: affinity === "None" ? "transparent" : accentColor }}
                />
            </div>
            <span className="ce-receptor-affinity">{affinity}</span>
        </div>
    );
}

function CompoundEducation({ setHeaderTitle }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const compound = compoundData[slug];

    useEffect(() => {
        if (setHeaderTitle) {
            setHeaderTitle(compound ? compound.name : "Compound");
        }
    }, [slug]);

    if (!compound) {
        return (
            <div className="ce-page">
                <button className="ce-back-btn" onClick={() => navigate(-1)}>← Back</button>
                <div className="ce-not-found">
                    <h2>Compound Not Found</h2>
                    <p>No educational data is available for <code>{slug}</code>.</p>
                </div>
            </div>
        );
    }

    const {
        name, abbreviation, type, subtype, formula, molecularWeight, boilingPoint,
        cas, psychoactive, description, effects, aroma, foundIn, receptors,
        researchAreas, relatedCompounds, accentColor,
    } = compound;

    const subtypeLabel = subtype === "major" ? "Major" : subtype === "minor" ? "Minor / Novel" : "Terpene";
    const typeLabel = type === "cannabinoid" ? "Cannabinoid" : "Terpene";

    return (
        <div className="ce-page">
            {/* Back */}
            <button className="ce-back-btn" onClick={() => navigate(-1)}>← Back</button>

            {/* Header */}
            <div className="ce-header">
                <div className="ce-header-badges">
                    <span
                        className="ce-badge ce-badge--type"
                        style={{ backgroundColor: accentColor }}
                    >
                        {typeLabel}
                    </span>
                    <span className="ce-badge ce-badge--subtype">{subtypeLabel}</span>
                    {psychoactive && (
                        <span className="ce-badge ce-badge--psychoactive">⚡ Psychoactive</span>
                    )}
                </div>
                <h1 className="ce-compound-name">{name}</h1>
                {abbreviation !== name && (
                    <span className="ce-abbreviation">{abbreviation}</span>
                )}

                {/* Key metrics strip */}
                <div className="ce-metrics-strip">
                    {formula && (
                        <div className="ce-metric">
                            <span className="ce-metric-label">Formula</span>
                            <span className="ce-metric-value ce-mono">{formula}</span>
                        </div>
                    )}
                    {molecularWeight && molecularWeight !== "—" && (
                        <div className="ce-metric">
                            <span className="ce-metric-label">Mol. Weight</span>
                            <span className="ce-metric-value ce-mono">{molecularWeight}</span>
                        </div>
                    )}
                    {boilingPoint && boilingPoint !== "—" && (
                        <div className="ce-metric">
                            <span className="ce-metric-label">Boiling Point</span>
                            <span className="ce-metric-value ce-mono">{boilingPoint}</span>
                        </div>
                    )}
                    {cas && cas !== "—" && (
                        <div className="ce-metric">
                            <span className="ce-metric-label">CAS</span>
                            <span className="ce-metric-value ce-mono">{cas}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Main content */}
            <div className="ce-body">
                {/* Left column */}
                <div className="ce-left">
                    <section className="ce-section">
                        <p className="ce-description">{description}</p>
                    </section>

                    {effects.length > 0 && (
                        <section className="ce-section">
                            <h3 className="ce-section-title">Effects &amp; Properties</h3>
                            <ul className="ce-list">
                                {effects.map((e) => (
                                    <li key={e}>{e}</li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {researchAreas.length > 0 && (
                        <section className="ce-section">
                            <h3 className="ce-section-title">Research Areas</h3>
                            <div className="ce-chip-row">
                                {researchAreas.map((r) => (
                                    <span key={r} className="ce-chip">{r}</span>
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                {/* Right column */}
                <div className="ce-right">
                    {/* Cannabinoid: receptor affinity */}
                    {receptors && (
                        <section className="ce-section ce-card">
                            <h3 className="ce-section-title">Receptor Affinity</h3>
                            <ReceptorBar label="CB1" affinity={receptors.CB1} accentColor={accentColor} />
                            <ReceptorBar label="CB2" affinity={receptors.CB2} accentColor={accentColor} />
                            <p className="ce-receptor-note">
                                CB1: primarily brain &amp; CNS · CB2: primarily immune &amp; peripheral
                            </p>
                        </section>
                    )}

                    {/* Terpene: aroma profile */}
                    {aroma.length > 0 && (
                        <section className="ce-section ce-card">
                            <h3 className="ce-section-title">Aroma Profile</h3>
                            <div className="ce-chip-row">
                                {aroma.map((a) => (
                                    <span
                                        key={a}
                                        className="ce-chip ce-chip--aroma"
                                        style={{ borderColor: accentColor, color: accentColor }}
                                    >
                                        {a}
                                    </span>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Found in */}
                    {foundIn.length > 0 && (
                        <section className="ce-section ce-card">
                            <h3 className="ce-section-title">Also Found In</h3>
                            <div className="ce-chip-row">
                                {foundIn.map((f) => (
                                    <span key={f} className="ce-chip">{f}</span>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>

            {/* Related compounds */}
            {relatedCompounds.length > 0 && (
                <section className="ce-related">
                    <h3 className="ce-section-title">Synergistic Compounds</h3>
                    <div className="ce-chip-row">
                        {relatedCompounds.map((slug) => {
                            const related = compoundData[slug];
                            if (!related) return null;
                            return (
                                <button
                                    key={slug}
                                    className="ce-related-chip"
                                    style={{ borderColor: related.accentColor, color: related.accentColor }}
                                    onClick={() => navigate(`/compound/${slug}`)}
                                >
                                    {related.abbreviation || related.name}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}

export default CompoundEducation;
